'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/prob-extra'

const LikelihoodCurve = dynamic(() => import('../_components/widgets/ProbExtraWidgets').then(m => m.LikelihoodCurve), { ssr: false })

export default function MaximumLikelihood() {
  return (
    <Lesson>
      <Step>
        <H>Turning the question around</H>
        <P>
          So far we’ve asked: given a coin with a known bias, how likely are these flips? Machine learning asks the reverse.
          Given the flips we saw, which bias is the most believable?
        </P>
        <P>
          The trick: for every possible bias <M>p</M>, compute how likely the observed flips would be. That number, viewed as a
          function of <M>p</M>, is the <strong>likelihood</strong>. Pick the <M>p</M> where it peaks. That’s the{' '}
          <strong>maximum likelihood estimate</strong>.
        </P>
        <MB>{'L(p) = p^{\\#\\text{heads}}\\,(1 - p)^{\\#\\text{tails}}'}</MB>
        <Figure n="5.12" wide caption="Each bias p gives the flips some probability. Drag p to explore; the peak is the most likely bias. Add flips and watch the peak sharpen.">
          <LikelihoodCurve />
        </Figure>
      </Step>

      <Step gate={['q-mle', 'coin-likelihood']}>
        <H>Why logs again</H>
        <P>
          Multiplying many probabilities gives numbers too small to store (Module 2). So we maximise the{' '}
          <strong>log-likelihood</strong> instead. The log turns the product into a sum, and since log only ever goes up, the
          peak stays in the same place.
        </P>
        <MB>{'\\log L(p) = \\#\\text{heads} \\cdot \\log p + \\#\\text{tails} \\cdot \\log(1 - p)'}</MB>
        <Quiz
          id="q-mle"
          question="You flip a coin 4 times and get 4 heads. What’s the maximum likelihood estimate of P(heads)?"
          options={[
            { label: '1 — it’s the p that makes four heads most likely', correct: true },
            { label: '0.5 — coins are fair', why: 'That’s a prior belief, not what the data alone says. MLE only listens to the data.' },
            { label: '0.8', why: 'Try the widget: add only heads. The peak goes all the way to p = 1.' },
          ]}
          explain="With little data, MLE can be overconfident: it says this coin can never land tails. Combining it with a prior (Bayes!) or adding smoothing fixes that — you’ll do exactly this in the spam filter project."
        />
        <Exercise
          id="coin-likelihood"
          title="logLikelihood and bestP"
          {...EX.coinLikelihood}
          hints={[
            <>
              <C>{'for (const f of flips) total += f === "H" ? Math.log(p) : Math.log(1 - p)'}</C>
            </>,
            <>
              Loop <C>i</C> from 1 to 999, <C>{'const p = i / 1000'}</C>, and keep the best.
            </>,
          ]}
        />
      </Step>

      <Step gate="gaussian-mle">
        <H>Where squared error comes from</H>
        <P>
          Now suppose measurements are a true value <M>{'\\mu'}</M> plus bell-curve noise. Which <M>{'\\mu'}</M> is most likely?
          Take the log of the bell curve and something remarkable appears:
        </P>
        <MB>{'\\log L(\\mu) = -\\frac{1}{2\\sigma^2}\\sum_i (x_i - \\mu)^2 + \\text{constant}'}</MB>
        <P>
          Maximising that is the same as <em>minimising the sum of squared errors</em>. So the mean squared error you used to
          fit curves wasn’t an arbitrary choice: it’s maximum likelihood, assuming bell-curve noise.
        </P>
        <Exercise
          id="gaussian-mle"
          title="logLik and bestMu"
          {...EX.gaussianMLE}
          hints={[
            <>
              <C>{'total += -((x - mu) ** 2) / (2 * sigma * sigma) - Math.log(sigma * Math.sqrt(2 * Math.PI))'}</C>
            </>,
            <>
              Try <C>{'mu = i / 1000'}</C> for <C>i</C> from 0 to 10 000.
            </>,
          ]}
        />
      </Step>

      <Step gate={['categorical-mle', 'q-ce']}>
        <H>Where cross-entropy comes from</H>
        <P>
          Last one, and it’s the big one. A language model assigns a probability to every possible next token. The likelihood
          of some training text is the product of the probabilities the model gave to each token that actually came next. Take
          the log, flip the sign, average over tokens:
        </P>
        <MB>{'-\\frac{1}{n}\\log L = -\\frac{1}{n}\\sum_{i} \\log P_{\\text{model}}(\\text{token}_i)'}</MB>
        <P>
          That’s exactly the <strong>cross-entropy loss</strong> from the last lesson. Minimising cross-entropy <em>is</em>{' '}
          maximum likelihood. And for a simple model with one probability per symbol, the best answer is just counting.
        </P>
        <Exercise
          id="categorical-mle"
          title="fit and nll"
          {...EX.categoricalMLE}
          hints={[
            <>
              Count each character in a Map, then divide every count by <C>text.length</C>.
            </>,
            <>
              <C>{'for (const c of text) total -= Math.log(model.get(c) ?? 0)'}</C>, then divide by <C>text.length</C>.
            </>,
          ]}
        />
        <Quiz
          id="q-ce"
          question="Training a large language model by minimising cross-entropy means…"
          options={[
            { label: 'Finding the parameters that make the training text as likely as possible', correct: true },
            { label: 'Making the model’s answers as short as possible', why: 'Cross-entropy is about probabilities, not length.' },
            { label: 'Counting how often each word appears', why: 'That’s the answer for the simplest possible model. Big models can’t be solved by counting, so they use gradient descent.' },
          ]}
          explain="Squared error, cross-entropy and plenty of other losses are all maximum likelihood with different assumptions about the noise. That’s the unifying idea behind most of machine learning."
        />
        <Callout kind="why">
          You’ll see the counting answer again in Module 8: a bigram language model is exactly this “count and divide”, one
          table row per previous character.
        </Callout>
      </Step>
    </Lesson>
  )
}
